/**
 * Marker Icon Management with Fallbacks
 */

import { normalizeNetworkName } from '../../../common/components/Map/utils/filterUtils';

// Icon mapping - matching your folder structure
const ICON_MAPPING = {
  'iocl': 'IOCL',
  'hp': 'HP',
  'bpcl': 'BPCL',
  'tata': 'Tata',
  'ather': 'Ather',
  'jio_bp': 'JioBP',
  'volttic': 'Volttic',
  'zeon': 'Zeon',
  'relux': 'Relux',
  'statiq': 'Statiq',
  'zynka': 'Zynka',
  'chargezone': 'ChargeZone',
  'generic': 'Generic',
  'other': 'Generic'
};

// Cache for loaded icons to avoid repeated 404s
const iconCache = new Map();

/**
 * Get network name from charger object
 */
export function getNetworkName(charger) {
  const possibleFields = [
    charger?.name,
    charger?.chargepoint_name,
    charger?.operator,
    charger?.provider,
    charger?.owner,
    charger?.network,
    charger?.team_name,
  ].filter(Boolean);

  for (const field of possibleFields) {
    const normalized = normalizeNetworkName(field);
    if (normalized && normalized !== 'other' && normalized !== 'generic') {
      return normalized;
    }
  }

  return 'generic';
}

/**
 * Get icon path for network
 */
export function getNetworkIconPath(networkName) {
  const iconName = ICON_MAPPING[networkName] || 'Generic';
  
  // Special case for Zynka - uses PNG
  if (networkName === 'zynka') {
    return '/assets/image/networkFilter/Zynka.png';
  }
  
  return `/assets/image/networkFilter/${iconName}.svg`;
}

/**
 * Check if icon exists (tries to load it once)
 */
export async function checkIconExists(url) {
  if (iconCache.has(url)) {
    return iconCache.get(url);
  }

  try {
    const response = await fetch(url, { method: 'HEAD' });
    const exists = response.ok;
    iconCache.set(url, exists);
    return exists;
  } catch {
    iconCache.set(url, false);
    return false;
  }
}

/**
 * Get marker icon configuration
 * Returns either custom icon or fallback to colored circle
 */
export function getMarkerIcon(charger, useCustomIcons = false) {
  if (!useCustomIcons) {
    // Use simple colored circles (always works)
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: '#4CAF50',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 8,
    };
  }

  // Try to use custom network icon
  const networkName = getNetworkName(charger);
  const iconPath = getNetworkIconPath(networkName);

  return {
    url: iconPath,
    scaledSize: new window.google.maps.Size(32, 32),
    anchor: new window.google.maps.Point(16, 16),
  };
}

/**
 * Get marker icon with fallback handling
 */
export async function getMarkerIconWithFallback(charger) {
  const networkName = getNetworkName(charger);
  const iconPath = getNetworkIconPath(networkName);
  
  // Check if icon exists
  const exists = await checkIconExists(iconPath);
  
  if (exists) {
    return {
      url: iconPath,
      scaledSize: new window.google.maps.Size(32, 32),
      anchor: new window.google.maps.Point(16, 16),
    };
  }
  
  // Fallback to colored circle
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: '#4CAF50',
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2,
    scale: 8,
  };
}

export default {
  getNetworkName,
  getNetworkIconPath,
  getMarkerIcon,
  getMarkerIconWithFallback,
};
